'use client';

import { Button, buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { type DateRange, DayPicker, type MonthCaptionProps } from 'react-day-picker';

const shortWeekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * YearPicker Component
 * Renders a grid of years for selection.
 *
 * Props:
 * - selectedDate: Date currently selected (month and year)
 * - onSelect: function to call with new Date when a year is selected
 * - onPrev: function to go to previous year range
 * - onNext: function to go to next year range
 * - disabledYear: function to determine if a year should be disabled
 */
function YearPicker({
  selectedDate,
  onSelect,
  onPrev,
  onNext,
  className,
  disabledYear,
}: {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  onPrev: () => void;
  onNext: () => void;
  className?: string;
  disabledYear?: (year: number) => boolean;
}) {
  const currentYear = selectedDate.getFullYear();
  const startingYear = currentYear - 4;

  return (
    <div
      className={cn('dark:bg-grey-1000 mx-auto h-fit w-full rounded-3xl bg-neutral-100', className)}
    >
      <div className="relative my-3 flex h-12 w-full items-center justify-between px-5">
        <p className="text-grey-700 text-sm dark:text-neutral-100">Select year</p>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
            onClick={onPrev}
            aria-label="Show previous year range"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
            onClick={onNext}
            aria-label="Show next year range"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div className="border-grey-100 mx-auto mb-3 max-w-[392px] items-center justify-center border-t">
        {Array.from({ length: 5 }, (_, row) => (
          <div className="flex items-center justify-center align-middle" key={row}>
            {Array.from({ length: 3 }, (_, col) => {
              const year = startingYear + row * 3 + col;
              const isSelected = year === selectedDate.getFullYear();
              const isDisabled = disabledYear ? disabledYear(year) : false;
              return (
                <div className="flex h-[62.4px] w-full items-center justify-center" key={year}>
                  <Button
                    variant={isSelected ? 'pink' : 'ghost'}
                    className={`text-grey-900 w-18 font-normal ${isSelected && 'font-medium text-neutral-100'}`}
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setFullYear(year);
                      onSelect(newDate);
                    }}
                    disabled={isDisabled}
                  >
                    {year}
                  </Button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * MonthPicker Component
 * Renders a grid of months for selection.
 *
 * Props:
 * - selectedDate: Date currently selected (month and year)
 * - onSelect: function to call with new Date when a month is selected
 * - disabledMonth: function to determine if a month should be disabled
 */
function MonthPicker({
  selectedDate,
  onSelect,
  className,
  disabledMonth,
  onYearClick,
  onPrev,
  onNext,
}: {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  className?: string;
  disabledMonth?: (monthIndex: number) => boolean;
  onYearClick?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const selectedMonth = selectedDate.getMonth();
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString('default', { month: 'short' })
  );
  return (
    <div
      className={cn('dark:bg-grey-1000 mx-auto h-fit w-full rounded-3xl bg-neutral-100', className)}
    >
      <div className="relative my-3 flex h-12 w-full items-center justify-between px-5">
        {onYearClick ? (
          <Button
            variant="ghost"
            onClick={onYearClick}
            className="text-neutral-200! dark:text-neutral-100! items-center justify-start p-0 align-middle text-sm leading-none font-normal"
          >
            {selectedDate.getFullYear()}
            <ChevronDown className="h-6! w-6!" />
          </Button>
        ) : (
          <p className="text-grey-700 text-sm dark:text-neutral-100">Select Month</p>
        )}
        {(onPrev || onNext) && (
          <div className="flex items-center">
            {onPrev && (
              <Button
                variant="ghost"
                className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
                onClick={onPrev}
                aria-label="Previous year"
              >
                <ChevronLeft />
              </Button>
            )}
            {onNext && (
              <Button
                variant="ghost"
                className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
                onClick={onNext}
                aria-label="Next year"
              >
                <ChevronRight />
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="border-grey-100 mx-auto mb-3 max-w-[392px] items-center justify-center border-t">
        {Array.from({ length: 4 }, (_, row) => (
          <div className="flex items-center justify-center align-middle" key={row}>
            {Array.from({ length: 3 }, (_, col) => {
              const monthIndex = row * 3 + col;
              const isSelected = monthIndex === selectedMonth;
              const isDisabled = disabledMonth ? disabledMonth(monthIndex) : false;
              return (
                <div
                  className="flex h-[62.4px] w-1/3 items-center justify-center px-1"
                  key={monthIndex}
                >
                  <Button
                    variant={isSelected ? 'pink' : 'ghost'}
                    className={`text-grey-900 w-full font-normal ${isSelected && 'font-medium text-neutral-100'}`}
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      // Set day to 1 first to prevent month overflow issues
                      // (e.g., Jan 31 -> setMonth(1) would become Mar 3)
                      newDate.setDate(1);
                      newDate.setMonth(monthIndex);
                      onSelect(newDate);
                    }}
                    disabled={isDisabled}
                  >
                    {months[monthIndex]}
                  </Button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  onSelect?: (date: Date | DateRange | undefined) => void;
}) {
  // Utility function to normalize dates to the 1st of the month
  // This prevents date rollover issues (e.g., Jan 31 + 1 month = Mar 2/3 instead of Feb 1)
  const normalizeToFirstOfMonth = React.useCallback((date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, []);

  // State for month/year pickers
  const [isYearPickerVisible, setIsYearPickerVisible] = React.useState(false);
  const [isMonthPickerVisible, setIsMonthPickerVisible] = React.useState(false);
  const [selectedMonthAndYear, setSelectedMonthAndYear] = React.useState<Date>(() => {
    if ('selected' in props && props.selected instanceof Date) {
      return normalizeToFirstOfMonth(new Date(props.selected));
    }
    if (props.defaultMonth instanceof Date) {
      return normalizeToFirstOfMonth(new Date(props.defaultMonth));
    }
    return normalizeToFirstOfMonth(new Date());
  });

  // Keep selectedMonthAndYear in sync with selected date or defaultMonth
  const selectedProp = 'selected' in props ? props.selected : undefined;
  React.useEffect(() => {
    if (selectedProp instanceof Date) {
      setSelectedMonthAndYear(normalizeToFirstOfMonth(new Date(selectedProp)));
    } else if (
      selectedProp &&
      typeof selectedProp === 'object' &&
      'from' in selectedProp &&
      selectedProp.from
    ) {
      setSelectedMonthAndYear(normalizeToFirstOfMonth(new Date(selectedProp.from)));
    } else if (props.defaultMonth instanceof Date && !selectedProp) {
      setSelectedMonthAndYear(normalizeToFirstOfMonth(new Date(props.defaultMonth)));
    }
  }, [selectedProp, props.defaultMonth, normalizeToFirstOfMonth]);

  // Show year picker
  if (isYearPickerVisible) {
    return (
      <YearPicker
        selectedDate={selectedMonthAndYear}
        onSelect={(newDate) => {
          const normalized = normalizeToFirstOfMonth(newDate);
          setSelectedMonthAndYear(normalized);
          setIsYearPickerVisible(false);
          setIsMonthPickerVisible(true);
          props.onMonthChange?.(normalized);
        }}
        onPrev={() => {
          const prevYears = normalizeToFirstOfMonth(
            new Date(selectedMonthAndYear.getFullYear() - 15, selectedMonthAndYear.getMonth())
          );
          setSelectedMonthAndYear(prevYears);
        }}
        onNext={() => {
          const nextYears = normalizeToFirstOfMonth(
            new Date(selectedMonthAndYear.getFullYear() + 15, selectedMonthAndYear.getMonth())
          );
          setSelectedMonthAndYear(nextYears);
        }}
      />
    );
  }

  // Show month picker
  if (isMonthPickerVisible) {
    return (
      <MonthPicker
        selectedDate={selectedMonthAndYear}
        onSelect={(newDate) => {
          const normalized = normalizeToFirstOfMonth(newDate);
          setSelectedMonthAndYear(normalized);
          setIsMonthPickerVisible(false);
          props.onMonthChange?.(normalized);
        }}
        onYearClick={() => {
          setIsMonthPickerVisible(false);
          setIsYearPickerVisible(true);
        }}
      />
    );
  }

  // Custom MonthCaption with dropdown
  const MonthCaption = (captionProps: MonthCaptionProps) => (
    <div className="relative my-3 flex h-12 w-full items-center justify-between">
      <Button
        aria-label="Choose month and year"
        aria-expanded={isMonthPickerVisible}
        onClick={() => setIsMonthPickerVisible(true)}
        variant="ghost"
        className="text-grey-700 items-center justify-start p-0 align-middle text-sm leading-none font-normal"
      >
        {captionProps.calendarMonth.date.toLocaleString('default', {
          month: 'long',
          year: 'numeric',
        })}
        <ChevronDown className="h-[18px]! w-[18px]!" />
      </Button>

      <div className="flex items-center justify-center">
        <Button
          variant="primary-icon"
          aria-label="Previous month"
          className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
          onClick={() => {
            const prevMonth = normalizeToFirstOfMonth(
              new Date(selectedMonthAndYear.getFullYear(), selectedMonthAndYear.getMonth() - 1, 1)
            );
            setSelectedMonthAndYear(prevMonth);
            props.onMonthChange?.(prevMonth);
          }}
        >
          <ChevronLeft className="h-4 w-4 text-lg" />
        </Button>
        <Button
          variant="primary-icon"
          aria-label="Next month"
          className="h-10! w-10! p-0! [&_svg]:h-6! [&_svg]:w-6!"
          onClick={() => {
            const nextMonth = normalizeToFirstOfMonth(
              new Date(selectedMonthAndYear.getFullYear(), selectedMonthAndYear.getMonth() + 1, 1)
            );
            setSelectedMonthAndYear(nextMonth);
            props.onMonthChange?.(nextMonth);
          }}
        >
          <ChevronRight className="h-4 w-4 text-lg" />
        </Button>
      </div>
    </div>
  );

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      hideNavigation
      className={cn('dark:bg-grey-1000 h-fit w-full rounded-3xl bg-neutral-100 px-5', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col flex-1',
        month_caption: 'flex justify-between my-3 h-12 relative items-center w-full',
        caption_label: 'text-sm text-grey-700',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          buttonVariants({ variant: 'primary-icon' }),
          'h-10 w-10 p-0! [&_svg]:w-6 [&_svg]:h-6'
        ),
        button_next: cn(
          buttonVariants({ variant: 'primary-icon' }),
          'h-10 w-10 p-0! [&_svg]:w-6 [&_svg]:h-6'
        ),
        month_grid: 'w-full border-collapse space-x-1 mb-3',
        weekdays: 'flex',
        weekday:
          'text-primary dark:text-neutral-100 rounded-md w-8 sm:w-10 h-12 justify-center items-center flex font-normal text-base flex-1',
        week: 'flex w-full h-12 flex justify-center items-center',
        day: cn(
          'relative p-0 flex-1 min-w-8 sm:min-w-10 text-center text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&.range-start[aria-selected=true]:not(.range-end)]:bg-[linear-gradient(to_right,transparent_50%,theme(colors.pink.50)_50%)] dark:[&.range-start[aria-selected=true]:not(.range-end)]:bg-[linear-gradient(to_right,transparent_50%,theme(colors.deep-plum.500)_50%)] [&.range-end[aria-selected=true]:not(.range-start)]:bg-[linear-gradient(to_left,transparent_50%,theme(colors.pink.50)_50%)] dark:[&.range-end[aria-selected=true]:not(.range-start)]:bg-[linear-gradient(to_left,transparent_50%,theme(colors.deep-plum.500)_50%)]'
            : ''
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8! sm:size-10! p-0! font-normal aria-selected:opacity-100 aria-selected:font-medium h-12 text-grey-900 dark:text-grey-50'
        ),
        // In v9, these classes are applied to the <td>, include class name for CSS selectors to detect
        range_start:
          'range-start [&>button]:bg-pink-400! [&>button]:text-neutral-100! [&>button]:font-medium [&>button]:rounded-full!',
        range_end:
          'range-end [&>button]:bg-pink-400! [&>button]:text-neutral-100! [&>button]:font-medium [&>button]:rounded-full!',
        selected:
          '[&>button]:bg-pink-400! [&>button]:text-neutral-100! [&>button]:font-medium [&>button]:rounded-full! [&>button]:hover:bg-pink-500',
        today:
          '[&:not([aria-selected=true])>button]:bg-transparent! [&:not([aria-selected=true])>button]:text-pink-400! dark:[&:not([aria-selected=true])>button]:text-pink-400! [&:not([aria-selected=true])>button]:border! [&:not([aria-selected=true])>button]:border-solid! [&:not([aria-selected=true])>button]:border-pink-400! [&:not([aria-selected=true])>button]:rounded-full!',
        outside: '[&>button]:text-grey-600! [&>button]:opacity-50',
        disabled: '[&>button]:text-muted-foreground! [&>button]:opacity-50',
        range_middle:
          'bg-pink-50! dark:bg-deep-plum-500! [&>button]:bg-transparent! [&>button]:text-grey-900! dark:[&>button]:text-neutral-100! [&>button]:font-medium',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        MonthCaption: MonthCaption,
      }}
      month={selectedMonthAndYear}
      onMonthChange={(month: Date) => {
        const normalized = normalizeToFirstOfMonth(month);
        setSelectedMonthAndYear(normalized);
        props.onMonthChange?.(normalized);
      }}
      formatters={{
        formatWeekdayName: (weekday) => shortWeekdays[weekday.getDay()] || '',
      }}
      {...props}
    />
  );
}

export { Calendar, MonthPicker, YearPicker };
